import { useMemo, useState, useEffect, useRef } from "react";
import { EditCompanysPlanFormInterface } from "../EditPlanPopup/EditPlanPopup.model";
import { usePlan } from "@/entities/Plan";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { useHeaderDashboard } from "../../../lib/useHeaderDashboard";
import { parseUsersInDepartment } from "@/shared/lib/users";
import { useHeaderDashboardActions } from "../../../lib/useHeaderDashboardActions";
import { UserInterface } from "@/shared/model/api.types";
import { Option } from '@/shared/components/common/ComboInputSelect/ComboInputSelect';
import { CompanyInterface } from "../../../model/HeaderDashboardStore.model";
import { usePlanActions } from "@/entities/Plan/lib/usePlanActions";
import planApi from "@/entities/Plan/api/plan.api";
import { useNotificationActions } from "@/features/Notification";
import { useAuth } from '@/features/Auth/lib/useAuth';
import { isSalesSystemAccount, salesAdministrationDepartments } from '@/features/Auth/lib/salesAccess';


const useEditCompanysPlanPopup = (id: number | null, onClose: () => void) => {
  const { myPlans, myMultyPlans, planTypes, loading: planTypesLoading, error: planTypesError,  } = usePlan();
  const { users, departments, subGenDepartments, loading: workspaceLoading, error: workspaceError } = useWorkspace();
  const { user } = useAuth();
  const availableDepartments = useMemo(
    () => isSalesSystemAccount(user) ? salesAdministrationDepartments(departments) : subGenDepartments,
    [departments, subGenDepartments, user]
  );
  const { fetchCompanies } = useHeaderDashboardActions();
  const { updateMultyplan, deleteMulty } = usePlanActions()
  const { loading: dashboardLoading, error: dashboardError } = useHeaderDashboard();
  const { topAlert, warningAlertWithConfirm } = useNotificationActions();
  const [dropdownValue, setDropdownValue] = useState<Option | Option[]>();
  const filteredPlanTypes = useMemo(() => planTypes.filter(pt => pt.code.includes('EMPLOYEE_MONTH') && !pt.code.includes('MULTY')), [planTypes]);
  const { setSelectedPlan } = useHeaderDashboard();

  const plan = id ? myPlans?.find(plan => plan.id === id) : null;

  const initForm = useMemo((): EditCompanysPlanFormInterface => {
    if (plan) {
      const { id, plan_type_id, year, month, target_value, user_id, department_id } = plan;
      const multyPlans = myMultyPlans.filter(mp => mp.plan_id === id);
      const grouped = multyPlans.reduce((acc, mp) => {
        if (!acc[mp.object]) acc[mp.object] = [];
        acc[mp.object].push({ plan_type_id: mp.plan_type_id, target_value: mp.target_value, id: mp.id });
        return acc;
      }, {} as Record<string, { plan_type_id: number, target_value: number, id: number }[]>);
      
      // Добавляем недостающие типы планов для каждой компании
      const multy = Object.entries(grouped).map(([object, target_values]) => {
        const existingPlanTypeIds = target_values.map(tv => tv.plan_type_id);
        const missingPlanTypes = filteredPlanTypes.filter(pt => !existingPlanTypeIds.includes(pt.id));
        const additionalTargetValues = missingPlanTypes.map(pt => ({
          plan_type_id: pt.id,
          target_value: 0,
          id: 0
        }));
        
        return {
          object,
          target_values: [...target_values, ...additionalTargetValues]
        };
      });

      
      setDropdownValue(Object.keys(grouped).map(object => ({ value: object, label: object })));
      return {
        id,
        plan_type_id,
        year,
        month,
        target_value,
        user_id,
        department_id,
        multy
      };
    }
    return {
      id: 0,
      plan_type_id: 0,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      target_value: 0,
      user_id: 0,
      department_id: 0,
      multy: [],
    };
  }, [plan, myMultyPlans, filteredPlanTypes]);

  

  const [form, setForm] = useState<EditCompanysPlanFormInterface>(initForm);
  const [filteredUsers, setFilteredUsers] = useState<UserInterface[]>([]);
  const [inputValue, setInputValue] = useState<string | number>('');
  const [options, setOptions] = useState<Option[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<{inn: string, label: string}[]>([]);

  useEffect(() => {
    setForm(initForm);
  }, [initForm]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputValue || (typeof inputValue === 'string' && !inputValue.trim())) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const res = await fetchCompanies(inputValue.toString());
      if (Array.isArray(res)) {
        setOptions(res.map((company: CompanyInterface) => ({
          value: company.inn,
          label: company.name,
        })));
      } else {
        setOptions([]);
      }
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    const usersResult = parseUsersInDepartment(
      users,
      form && "department_id" in form ? (form.department_id as number) : null
    );
    setFilteredUsers(prev => {
      const isSame = Array.isArray(usersResult) && Array.isArray(prev) && usersResult.length === prev.length && usersResult.every((v, i) => v.id === prev[i].id);
      return isSame ? prev : (usersResult ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, form.department_id, form]);

  useEffect(() => {
    setForm(initForm);
  }, [initForm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (!prev) return prev;
      const newForm = {
        ...prev,
        [name]:
          [
            "target_value",
            "year",
            "month",
            "plan_type_id",
            "user_id",
            "department_id",
          ].includes(name)
            ? Number(value)
            : value,
      };
      if (name === "user_id") {
        newForm.plan_type_id = 0;
      }
      return newForm;
    });
  };

  const handleChangeTargetValue = (inn: string, plan_type_id: number, value: number) => {
    setForm((prev) => ({
      ...prev,
      multy: prev.multy.map((m) =>
        m.object === inn
          ? {
              ...m,
              target_values: (() => {
                const existingIndex = m.target_values.findIndex(tv => tv.plan_type_id === plan_type_id);
                if (existingIndex !== -1) {
                  // Если запись существует, обновляем её
                  const updated = [...m.target_values];
                  updated[existingIndex] = { ...updated[existingIndex], target_value: value };
                  return updated;
                } else {
                  // Если записи нет, добавляем новую
                  return [...m.target_values, { plan_type_id, target_value: value, id: 0 }];
                }
              })()
            }
          : m
      )
    }));
  };

  const handleRemoveFromMulty = (inn: string) => {
    setForm((prev) => ({
      ...prev,
      multy: prev.multy.filter((m) => m.object !== inn),
    }));
    setDropdownValue((prev) => {
      if (Array.isArray(prev)) {
        return prev.filter((opt) => String(opt.value) !== String(inn));
      }
      if (prev && String((prev as Option).value) === String(inn)) {
        return [];
      }
      return prev;
    });
    setSelectedCompanies((prev) => prev.filter(c => c.inn !== inn));
  };



  useEffect(() => {
    let arr: Option[] = [];
    if (Array.isArray(dropdownValue)) arr = dropdownValue;
    else if (dropdownValue) arr = [dropdownValue];
    setForm((prev) => ({
      ...prev,
      multy: arr.map((item) => {
        const exist = prev.multy.find((m) => String(m.object) === String(item.value));
        if (exist) {
          return {
            object: String(item.value),
            target_values: exist.target_values,
          };
        } else {
          // Для новых компаний создаем пустые target_values для всех типов планов
          return {
            object: String(item.value),
            target_values: filteredPlanTypes.map(pt => ({
              plan_type_id: pt.id,
              target_value: 0,
              id: 0
            })),
          };
        }
      })
    }));
    setSelectedCompanies((prev) => {
      const newCompanies = arr
        .filter(item => !prev.some(c => c.inn === String(item.value)))
        .map(item => ({ inn: String(item.value), label: item.label }));
      const filtered = prev.filter(c => arr.some(item => String(item.value) === c.inn));
      return [...filtered, ...newCompanies];
    });
  }, [dropdownValue, filteredPlanTypes]);

  useEffect(() => {
    if (!dropdownValue || !Array.isArray(dropdownValue)) return;

    const fetchAllCompanies = async () => {
      for (const p of dropdownValue) {
        const res = await fetchCompanies(String(p.value));
        if (res && res[0] && res[0].name) {
          setDropdownValue(prev => {
            if (!Array.isArray(prev)) return prev;
            const idx = prev.findIndex((c: any) => String(c.value) === String(res[0].inn));
            if (idx !== -1) {
              // если нашли, заменяем
              const updated = [...prev];
              updated[idx] = {
                value: String(res[0].inn),
                label: `${res[0].name}`
              };
              return updated;
            }
            // если не нашли, добавляем
            return [
              ...prev,
              {
                value: String(res[0].inn),
                label: `${res[0].name}`
              }
            ];
          });
          setSelectedCompanies(prev => {
            const idx = prev.findIndex(c => c.inn === String(res[0].inn));
            if (idx !== -1) {
              // если нашли, заменяем
              const updated = [...prev];
              updated[idx] = {
                inn: String(res[0].inn),
                label: `${res[0].name}`
              };
              return updated;
            }
            // если не нашли, добавляем
            return [
              ...prev,
              {
                inn: String(res[0].inn),
                label: `${res[0].name}`
              }
            ];
          });
        }
      }
    };
    fetchAllCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initForm]);

  
  const handleDelete = async (e: React.FormEvent) => {
    warningAlertWithConfirm(
      "Вы уверены, что хотите удалить план?",
      async () => {
        const res = await deleteMulty(form.id)
        if(res){
          setSelectedPlan(null);
          onClose();
        }
      },
      () => {},
      "Удалить",
      "Отменить",
      undefined,
      "danger",
      "success"
    );
  }

  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  


  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // Валидация обязательных полей для multy
    if (
      !form.year || form.year === 0 ||
      !form.month || form.month === 0 ||
      !form.department_id || form.department_id === 0 ||
      !form.user_id || form.user_id === 0 ||
      !form.multy || form.multy.length === 0 ||
      form.multy.some(m => !m.object || !m.target_values || m.target_values.length === 0)
    ) {
      topAlert("Пожалуйста, заполните все обязательные поля", "warning", 1000);
      return;
    }
    warningAlertWithConfirm(
      "Вы уверены, что хотите сохранить изменения?",
      savePlan,
      () => {},
      "Сохранить",
      "Отменить"
    );
  };  

  const savePlan = async () => {
    const res = await updateMultyplan(form)
    if(res){
      onClose();
    }
  };

  return {
    form,
    users: filteredUsers,
    departments: availableDepartments,
    setInputValue: (val: string | number) => setInputValue(val),
    handleChange,
    loading: {
      users: workspaceLoading.users,
      departments: workspaceLoading.departments,
      companies: dashboardLoading.companies,
    },
    error: {
      users: workspaceError.users,
      departments: workspaceError.departments,
      companies: dashboardError.companies,
    },
    inputValue,
    options,
    dropdownValue,
    setDropdownValue,
    selectedCompanies,
    handleChangeTargetValue,
    handleRemoveFromMulty,
    planTypes: filteredPlanTypes,
    handleSave,
    handleDelete,
    setOpenCreatePopup,
    openCreatePopup,
    initForm
  };
};

export default useEditCompanysPlanPopup; 
